import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Tooltip,
  IconButton,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import {
  Calendar as CalendarIcon,
  FileText,
  Search,
  RefreshCw,
  BarChart3,
} from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../services/api';
import LoaderProgress from '../components/ui/loader-progress';
import { useJob } from '../context/JobContext';

const StyledCard = styled(Card)(({ theme }) => ({
  boxShadow:
    '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden',
}));

const StatWidget = ({ title, value, subtitle, icon: Icon, color }) => (
  <Paper
    sx={{
      p: 2,
      display: 'flex',
      alignItems: 'center',
      height: '100%',
      borderWidth: 1,
      borderStyle: 'solid',
      borderColor: 'rgba(145, 158, 171, 0.24)',
      boxShadow:
        '0 0 2px 0 rgba(145, 158, 171, 0.16), 0 12px 24px -4px rgba(145, 158, 171, 0.08)',
    }}
    elevation={0}
    variant="outlined"
  >
    <Box
      sx={{
        p: 1.5,
        borderRadius: '50%',
        bgcolor: `${color}15`,
        color,
        mr: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Icon size={20} />
    </Box>
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
        {title}
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
        {value}
      </Typography>
      {subtitle && (
        <Typography variant="body2" color="text.secondary">
          {subtitle}
        </Typography>
      )}
    </Box>
  </Paper>
);

export default function PasspConti() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('codice');
  const [totalItems, setTotalItems] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [azienda, setAzienda] = useState('all');
  const [anno, setAnno] = useState('all');
  const [aziendeOptions, setAziendeOptions] = useState([]);
  const [anniOptions, setAnniOptions] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [summary, setSummary] = useState({
    total: 0,
    bil34DirUeCount: 0,
    bilIvDirCeeCount: 0,
    bilXbrlRsmCount: 0,
    preCancellatiCount: 0,
  });
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncAzienda, setSyncAzienda] = useState('');
  const activeRequestIdRef = useRef(0);
  const activeStatsRequestIdRef = useRef(0);
  const { addJob, jobs } = useJob();

  const totalColumns = 8;
  const currentYear = useMemo(() => new Date().getFullYear(), []);
  const [syncAnno, setSyncAnno] = useState(String(currentYear));

  const renderAziendaLabel = (item) => {
    if (!item) return '';
    const { name, firstYear, lastYear } = item;
    if (firstYear && lastYear) {
      return `${name} (${firstYear} - ${lastYear})`;
    }
    if (firstYear) {
      return `${name} (${firstYear} - ...)`;
    }
    if (lastYear) {
      return `${name} (... - ${lastYear})`;
    }
    return name;
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (_event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearchInput(event.target.value);
  };

  const handleFilterAzienda = (event) => {
    setAzienda(event.target.value);
    setPage(0);
  };

  const handleFilterAnno = (event) => {
    setAnno(event.target.value);
    setPage(0);
  };

  const handleToggleFilters = () => {
    setShowFilters((prev) => !prev);
  };

  const handleOpenSyncDialog = () => {
    setSyncAzienda('');
    setSyncAnno(String(currentYear));
    setSyncDialogOpen(true);
  };

  const handleCloseSyncDialog = () => {
    if (syncing) return;
    setSyncDialogOpen(false);
  };

  const handleConfirmSync = async () => {
    if (!syncAzienda || !syncAnno) {
      toast.error('Seleziona azienda e anno per la sincronizzazione');
      return;
    }

    const yearInt = parseInt(syncAnno, 10);
    if (!yearInt || Number.isNaN(yearInt)) {
      toast.error('Inserisci un anno di esercizio valido');
      return;
    }

    setSyncing(true);
    const pendingToastId = toast.loading('Avvio sincronizzazione Conti Passpartout...');
    try {
      const res = await api.post('/passp-conti/sync', {
        azienda: syncAzienda,
        anno: yearInt,
      });
      const jobId = res.data?.jobId;
      const startMessage =
        res.data?.message ||
        `Sincronizzazione Conti Passpartout avviata per azienda ${syncAzienda}, anno ${yearInt}`;
      if (jobId) {
        addJob(jobId, 'passp_conti_sync', { silent: true });
        toast.update(pendingToastId, {
          render: startMessage,
          type: 'success',
          isLoading: false,
          autoClose: 2500,
        });
      } else {
        toast.update(pendingToastId, {
          render: startMessage,
          type: 'success',
          isLoading: false,
          autoClose: 2500,
        });
      }
      setSyncDialogOpen(false);
      setAzienda(syncAzienda);
      setAnno(String(yearInt));
      setPage(0);
      fetchData(true);
      fetchStats(true);
      fetchFilters();
    } catch (error) {
      console.error('Error syncing Passpartout conti:', error);
      toast.update(pendingToastId, {
        render: 'Errore durante la sincronizzazione Conti Passpartout',
        type: 'error',
        isLoading: false,
        autoClose: 4000,
      });
    } finally {
      setSyncing(false);
    }
  };

  const fetchFilters = async () => {
    try {
      const res = await api.get('/passp-conti/filters/options');
      const data = res.data || {};
      setAziendeOptions((data.aziende || []).filter(Boolean));
      setAnniOptions((data.anni || []).filter(Boolean));
    } catch (error) {
      console.error('Error fetching Passpartout conti filters:', error);
      toast.error('Errore nel caricamento dei filtri Conti Passpartout');
    }
  };

  const fetchData = async (force = false) => {
    const requestId = activeRequestIdRef.current + 1;
    activeRequestIdRef.current = requestId;
    if (!force) {
      setLoading(true);
    }
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        orderBy,
        orderDirection: order,
      };
      if (searchValue) params.search = searchValue;
      if (azienda !== 'all') params.azienda = azienda;
      if (anno !== 'all') params.anno = anno;

      const res = await api.get('/passp-conti', { params });
      if (activeRequestIdRef.current !== requestId) {
        return;
      }
      const data = res.data || {};
      setRows(Array.isArray(data.data) ? data.data : []);
      setTotalItems(data.total || 0);
    } catch (error) {
      console.error('Error fetching Passpartout conti:', error);
      toast.error('Errore nel caricamento Conti Passpartout');
    } finally {
      if (activeRequestIdRef.current === requestId) {
        setLoading(false);
      }
    }
  };

  const fetchStats = async (force = false) => {
    const requestId = activeStatsRequestIdRef.current + 1;
    activeStatsRequestIdRef.current = requestId;
    try {
      const params = {};
      if (searchValue) params.search = searchValue;
      if (azienda !== 'all') params.azienda = azienda;
      if (anno !== 'all') params.anno = anno;

      const res = await api.get('/passp-conti/stats', { params });
      if (activeStatsRequestIdRef.current !== requestId && !force) {
        return;
      }
      const data = res.data || {};
      setSummary({
        total: data.total || 0,
        bil34DirUeCount: data.bil34DirUeCount || 0,
        bilIvDirCeeCount: data.bilIvDirCeeCount || 0,
        bilXbrlRsmCount: data.bilXbrlRsmCount || 0,
        preCancellatiCount: data.preCancellatiCount || 0,
      });
    } catch (error) {
      console.error('Error fetching Passpartout conti stats:', error);
    }
  };

  useEffect(() => {
    fetchFilters();
  }, []);

  useEffect(() => {
    fetchData();
  }, [page, rowsPerPage, order, orderBy, searchValue, azienda, anno]);

  useEffect(() => {
    fetchStats();
  }, [searchValue, azienda, anno]);

  useEffect(() => {
    const syncJobs = jobs.filter(
      (j) => j.type === 'passp_conti_sync' && j.status === 'completed'
    );
    if (syncJobs.length > 0) {
      setPage(0);
      fetchData(true);
      fetchStats(true);
      fetchFilters();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobs]);

  useEffect(() => {
    const trimmed = searchInput.trim();
    const handler = setTimeout(() => {
      setPage(0);
      if (trimmed.length >= 2) {
        setSearchValue(trimmed);
      } else {
        setSearchValue('');
      }
    }, 400);
    return () => clearTimeout(handler);
  }, [searchInput]);

  const empty = !loading && rows.length === 0;

  const bil34Label = useMemo(() => {
    if (!summary.total) return '-';
    const pct = (summary.bil34DirUeCount / summary.total) * 100;
    return `${summary.bil34DirUeCount} conti • ${pct.toFixed(1)}%`;
  }, [summary]);

  const bilIvDirLabel = useMemo(() => {
    if (!summary.total) return '-';
    const pct = (summary.bilIvDirCeeCount / summary.total) * 100;
    return `${summary.bilIvDirCeeCount} conti • ${pct.toFixed(1)}%`;
  }, [summary]);

  const bilXbrlLabel = useMemo(() => {
    if (!summary.total) return '-';
    const pct = (summary.bilXbrlRsmCount / summary.total) * 100;
    return `${summary.bilXbrlRsmCount} conti • ${pct.toFixed(1)}%`;
  }, [summary]);

  const preCancLabel = useMemo(() => {
    if (!summary.total) return '-';
    const pct = (summary.preCancellatiCount / summary.total) * 100;
    return `${summary.preCancellatiCount} conti • ${pct.toFixed(1)}%`;
  }, [summary]);

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: '#eef2ff',
              color: '#4f46e5',
            }}
          >
            <FileText size={22} />
          </Box>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
              Conti di bilancio Passpartout
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Piano dei conti sincronizzato dal gestionale Passpartout
            </Typography>
          </Box>
        </Box>

        <Button
          variant="contained"
          onClick={handleOpenSyncDialog}
          disabled={syncing}
          startIcon={
            syncing ? (
              <LoaderProgress size={20} intervalMs={80} step={15} maxBeforeDone={90} />
            ) : (
              <RefreshCw size={18} />
            )
          }
          sx={{
            textTransform: 'none',
            px: 2.5,
            py: 0.75,
          }}
        >
          {syncing ? 'Sincronizzazione...' : 'Sincronizza'}
        </Button>
      </Box>

      <Dialog
        open={syncDialogOpen}
        onClose={handleCloseSyncDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1,
              mt: 1,
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '999px',
                bgcolor: 'rgba(34, 197, 94, 0.12)',
                color: '#16A34A',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <BarChart3 size={22} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Sincronizza conti
            </Typography>
            <Typography variant="body2" color="text.secondary" align="center">
              Seleziona azienda e anno di esercizio da sincronizzare con Passpartout.
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl size="small" fullWidth>
              <InputLabel>Azienda</InputLabel>
              <Select
                value={syncAzienda}
                label="Azienda"
                onChange={(e) => setSyncAzienda(e.target.value)}
              >
                {aziendeOptions.map((item) => (
                  <MenuItem key={item.code} value={item.code}>
                    {renderAziendaLabel(item)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Anno esercizio"
              type="number"
              size="small"
              fullWidth
              value={syncAnno}
              onChange={(e) => setSyncAnno(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CalendarIcon size={16} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSyncDialog}>Annulla</Button>
          <Button onClick={handleConfirmSync} variant="contained" disabled={syncing}>
            Conferma
          </Button>
        </DialogActions>
      </Dialog>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatWidget
            title="Conti totali filtrati"
            value={summary.total || 0}
            icon={FileText}
            color="#2563EB"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatWidget
            title="Bilancio 34ª Dir. UE"
            value={bil34Label}
            icon={BarChart3}
            color="#16A34A"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatWidget
            title="Bilancio IV Dir. CEE"
            value={bilIvDirLabel}
            icon={BarChart3}
            color="#CA8A04"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatWidget
            title="XBRL / RSM"
            value={bilXbrlLabel}
            icon={BarChart3}
            color="#DC2626"
          />
        </Grid>
      </Grid>

      <StyledCard>
        <Box
          sx={{
            px: 3,
            py: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <TextField
              size="small"
              placeholder="Cerca per codice, descrizione, classificazione..."
              value={searchInput}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={18} />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 260 }}
            />

            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Azienda</InputLabel>
              <Select
                value={azienda}
                label="Azienda"
                onChange={handleFilterAzienda}
              >
                <MenuItem value="all">Tutte</MenuItem>
                {aziendeOptions.map((item) => (
                  <MenuItem key={item.code} value={item.code}>
                    {renderAziendaLabel(item)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title="Mostra filtri avanzati">
              <IconButton size="small" onClick={handleToggleFilters}>
                <FilterAltIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {showFilters && (
          <Box
            sx={{
              px: 3,
              py: 2,
              borderBottom: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Anno</InputLabel>
              <Select
                value={anno}
                label="Anno"
                onChange={handleFilterAnno}
              >
                <MenuItem value="all">Tutti</MenuItem>
                {anniOptions.map((item) => (
                  <MenuItem key={item} value={item}>
                    {item}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'background.neutral' }}>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'codice'}
                    direction={orderBy === 'codice' ? order : 'asc'}
                    onClick={() => handleRequestSort('codice')}
                  >
                    Codice
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'ragione_sociale'}
                    direction={orderBy === 'ragione_sociale' ? order : 'asc'}
                    onClick={() => handleRequestSort('ragione_sociale')}
                  >
                    Descrizione conto
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'azienda'}
                    direction={orderBy === 'azienda' ? order : 'asc'}
                    onClick={() => handleRequestSort('azienda')}
                  >
                    Azienda
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'anno'}
                    direction={orderBy === 'anno' ? order : 'desc'}
                    onClick={() => handleRequestSort('anno')}
                  >
                    Anno
                  </TableSortLabel>
                </TableCell>
                <TableCell>Cod. alternativo</TableCell>
                <TableCell>Class. bilancio (34ª UE)</TableCell>
                <TableCell>IV Dir. CEE</TableCell>
                <TableCell>XBRL / RSM</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={totalColumns}>
                    <LoaderProgress />
                  </TableCell>
                </TableRow>
              )}
              {empty && !loading && (
                <TableRow>
                  <TableCell colSpan={totalColumns}>
                    <Box
                      sx={{
                        py: 6,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                      }}
                    >
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        Nessun conto trovato
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Modifica i filtri o sincronizza i conti dal gestionale Passpartout.
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
              {!loading &&
                rows.map((row) => {
                  const bil34 = row.bil_34dir_ue_d || row.bil_34dir_ue_a || '-';
                  const bilIv = row.bil_ivdir_cee_d || row.bil_ivdir_cee_a || '-';
                  const bilXbrl =
                    row.bil_xbrl_rsm_d || row.bil_xbrl_rsm_a || '-';
                  return (
                    <TableRow key={row.id}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {row.codice || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {row.ragione_sociale || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {row.azienda || '-'}
                        </Typography>
                        {row.azienda_ragione_sociale && (
                          <Typography variant="caption" color="text.secondary">
                            {row.azienda_ragione_sociale}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{row.anno || '-'}</TableCell>
                      <TableCell>{row.cod_alternativo || '-'}</TableCell>
                      <TableCell>{bil34}</TableCell>
                      <TableCell>{bilIv}</TableCell>
                      <TableCell>{bilXbrl}</TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={totalItems}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Righe per pagina:"
        />
      </StyledCard>
    </Box>
  );
}
